import {
  AgeCategorySerializerSmall,
  CompetitionSerializerSmall,
  CompetitionType,
  RegistrationCategory,
  WeightClassResult,
} from '~/apis/ssf';
import { createQueryKeySet } from '~/utils/query-keys';
import { useQuery } from '@tanstack/react-query';
import { SSF } from '~/apis/api';
import { useMemo } from 'react';
import { uniqBy } from 'lodash';

export interface NormalizedRegistrationCategory {
  age_category: AgeCategorySerializerSmall;
  competition: CompetitionSerializerSmall;
  start_date: string | undefined | null;
  end_date: string | undefined | null;
  weight_classes: (WeightClassResult & {
    registration_category_id: number;
    limit: number | undefined | null;
  })[];
  competition_type: CompetitionType;
  is_male: boolean;
}

export const keys = {
  ...createQueryKeySet<number>('registration_categories'),
  summary: (id: number) => [...keys.list(id), 'summary'],
};

function tryFindDate(
  categories: RegistrationCategory[],
  competitionTypeId: number,
  ageCategoryId: number,
  isMale: boolean,
  field: 'start_date' | 'end_date'
): string | undefined | null {
  const result = categories.find(
    c =>
      c.competition_type.id === competitionTypeId &&
      c.age_category.id === ageCategoryId &&
      c.weight_class?.is_male === isMale &&
      c[field]
  );

  return result?.[field];
}

export function useCompetitionRegistrationCategoryListQuery(id: number) {
  return useQuery({
    queryFn: async () => {
      const { data } = await SSF.api.competitionsGetRegistrationCategoriesRetrieve(id);
      return data;
    },
    queryKey: keys.list(id),
  });
}

export function useNormalizedCompetitionRegistrationCategoryListQuery(id: number) {
  const { data: categories, ...query } = useCompetitionRegistrationCategoryListQuery(id);

  const data = useMemo<NormalizedRegistrationCategory[] | undefined>(() => {
    if (!categories) {
      return undefined;
    }

    if (!categories.length) {
      return [];
    }

    const result: NormalizedRegistrationCategory[] = [];
    const competition = categories.find(a => a)!.competition;

    /**
     * Extract unique competition types
     */
    const uniqCompetitionTypes = uniqBy(
      categories.map(c => c.competition_type),
      ct => ct.id
    );

    for (const competitionType of uniqCompetitionTypes) {
      /**
       * Extract unique age categories for current competition type
       */
      const uniqAgeCategories = uniqBy(
        categories.filter(rc => rc.competition_type.id === competitionType.id).map(rc => rc.age_category),
        ac => ac.id
      );

      for (const ageCategory of uniqAgeCategories) {
        for (const sex of [true, false]) {
          /**
           * Extract weight classes by age category, competition type and sex
           */
          const weightClasses = categories
            .filter(
              c =>
                c.competition_type.id === competitionType.id &&
                c.age_category.id === ageCategory.id &&
                c.weight_class?.is_male === sex
            )
            .map(c => {
              const search = categories.find(
                a =>
                  a.competition_type.id === competitionType.id &&
                  a.age_category.id === ageCategory.id &&
                  a.weight_class?.is_male === sex &&
                  a.weight_class?.id === c.weight_class?.id
              );

              if (!search) {
                throw new Error('An unexpected error occurred, unable to find registration category id');
              }

              return {
                registration_category_id: search.id,
                limit: search.limit,
                ...c.weight_class!,
              };
            });

          result.push({
            start_date: tryFindDate(categories, competitionType.id, ageCategory.id, sex, 'start_date'),
            end_date: tryFindDate(categories, competitionType.id, ageCategory.id, sex, 'end_date'),
            competition_type: competitionType,
            weight_classes: weightClasses,
            age_category: ageCategory,
            competition: competition,
            is_male: sex,
          });
        }
      }
    }

    return result;
  }, [categories]);

  return {
    ...query,
    data,
  };
}

export function useCompetitionRegistrationCategorySummaryQuery(id: number, enabled = true) {
  return useQuery({
    queryFn: async () => {
      const { data } = await SSF.api.competitionsGetRegistrationCategoriesSummaryList(id);
      return data;
    },
    queryKey: keys.summary(id),
    enabled,
  });
}
